import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedStudent } from "@/State/usersSlice";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { School, Settings, UserPlus, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { auth, functions, db } from "../firebase/config";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import Teacherheader from "./GuardianHeader";
import Teachersidebar from "./GuardianSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import ConfirmDialog from "@/components/ConfirmDialog";
import { setDoc } from "firebase/firestore";

type SchoolItem = { id: string; schoolName: string; role: string; inviteId: string };
type StudentItem = {
  studentUid: string;
  registrationNumber: string;
  firstName: string;
  lastName: string;
  email: string;
  dateOfBirth: string;
  gender: string;
  admissionGrade: string;
  className: string;
  dormitory: string;
  medicalInfo?: string;
  otherInfo?: string;
  status: string;
  attendance: number;
};
interface Affiliation {
  role: string;
  schoolID?: string;
}
interface CustomClaims {
  affiliations?: Affiliation[];
  defaultAffiliation?: Affiliation;
  schoolID?: string;
}

const ParentSettings = () => {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const currentUser = useSelector((state: any) => state.users?.currentUser);
  const selectedStudent = currentUser?.selectedStudent;

  const [email, setEmail] = useState("");
  const [schools, setSchools] = useState<SchoolItem[]>([]);
  const [enrolledSchools, setEnrolledSchools] = useState<SchoolItem[]>([]);
  const [selectedSchool, setSelectedSchool] = useState("");
  const [students, setStudents] = useState<StudentItem[]>([]);
  const [loadingSchools, setLoadingSchools] = useState(true);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [hasValidClaims, setHasValidClaims] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [enrolledSchoolName, setEnrolledSchoolName] = useState("");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingStudentSelection, setPendingStudentSelection] = useState<StudentItem | null>(null);

  useEffect(() => {
    console.log("Auth useEffect triggered");
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log("Auth state changed:", { 
        user: user ? { uid: user.uid, email: user.email } : null,
        emailSet: !!user?.email,
        isAuthenticatedBefore: isAuthenticated
      });
      if (user) {
        setEmail(user.email || "");
        setIsAuthenticated(true);
        try {
          const token = await getIdTokenResult(user, true);
          const claims: CustomClaims = token.claims as CustomClaims;
          console.log("User claims:", {
            affiliations: claims.affiliations,
            defaultAffiliation: claims.defaultAffiliation,
            schoolID: claims.schoolID
          });

          const guardianAff = claims.defaultAffiliation?.role === "guardian" && claims.defaultAffiliation.schoolID
            ? claims.defaultAffiliation
            : claims.affiliations?.find(aff => aff.role === "guardian" && aff.schoolID);

          if (guardianAff && guardianAff.schoolID === claims.schoolID) {
            console.log("Valid guardian claims found:", { schoolID: guardianAff.schoolID });
            setHasValidClaims(true);
          } else {
            console.log("No valid guardian claims found");
            setHasValidClaims(false);
          }

          // Fetch enrolled schools from claims
          const enrolledList: SchoolItem[] = [];
          const allAffiliations = claims.affiliations || [];
          if (claims.defaultAffiliation) {
            allAffiliations.push(claims.defaultAffiliation);
          }

          // Filter unique guardian affiliations
          const uniqueAffiliations = allAffiliations.filter(
            (aff, index, self) =>
              aff.role === "guardian" &&
              aff.schoolID &&
              index === self.findIndex((t) => t.schoolID === aff.schoolID && t.role === aff.role)
          );

          for (const aff of uniqueAffiliations) {
            if (aff.schoolID) {
              try {
                let schoolName = "Unknown School";
                
                // 1. Try fetching from 'main' document (standard location)
                const schoolDoc = await getDoc(doc(db, "schools", aff.schoolID, "schoolDetails", "main"));
                
                if (schoolDoc.exists()) {
                  schoolName = schoolDoc.data().name || "Unknown School";
                } else {
                  // 2. Fallback: Fetch from collection if 'main' doesn't exist (legacy support)
                  const schoolDetailsRef = collection(db, "schools", aff.schoolID, "schoolDetails");
                  const snapshot = await getDocs(schoolDetailsRef);
                  if (!snapshot.empty) {
                    schoolName = snapshot.docs[0].data().name || "Unknown School";
                  }
                }

                enrolledList.push({
                  id: aff.schoolID,
                  schoolName: schoolName,
                  role: "guardian",
                  inviteId: "" // Not needed for display
                });
              } catch (error) {
                console.error("Error fetching school details:", error);
                enrolledList.push({
                  id: aff.schoolID,
                  schoolName: "Unknown School",
                  role: "guardian",
                  inviteId: ""
                });
              }
            }
          }
          setEnrolledSchools(enrolledList);

        } catch (error: any) {
          console.error("Claims validation error:", {
            message: error.message,
            code: error.code,
            details: error.details
          });
          setHasValidClaims(false);
        }
      } else {
        console.log("No user authenticated, resetting states");
        setIsAuthenticated(false);
        setEmail("");
        setSchools([]);
        setStudents([]);
        setHasValidClaims(false);
        setEnrolledSchools([]);
      }
    });
    return () => {
      console.log("Cleaning up auth listener");
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    console.log("Schools fetch useEffect triggered:", { email, isAuthenticated });
    const fetchSchools = async () => {
      try {
        if (!email || !isAuthenticated) {
          console.log("Skipping fetchSchools: missing email or not authenticated", { email, isAuthenticated });
          return;
        }
        console.log("Fetching pending guardian invitations for email:", email.toLowerCase());
        
        const getUserInvitations = httpsCallable(functions, 'getUserInvitations');
        const result = await getUserInvitations();
        const data = result.data as { invitations: any[] };
        
        const schoolList: SchoolItem[] = [];
        
        if (data.invitations) {
          for (const inviteData of data.invitations) {
            // Filter for guardian role and pending status
            if (inviteData.role === "guardian" && inviteData.status === "pending") {
              const schoolID = inviteData.schoolID;
              const schoolName = inviteData.schoolName || inviteData.originalSchoolName || "Unnamed School";
              console.log("Processing invitation:", {
                inviteId: inviteData.id,
                schoolID,
                schoolName,
                role: inviteData.role,
                status: inviteData.status,
                studentUid: inviteData.studentUid,
                isSchoolIDValid: !!schoolID && typeof schoolID === 'string'
              });

              if (schoolID && typeof schoolID === 'string' && !schoolList.some((s) => s.id === schoolID)) {
                console.log("Adding school to list:", { id: schoolID, schoolName });
                schoolList.push({ 
                  id: schoolID, 
                  schoolName, 
                  role: inviteData.role,
                  inviteId: inviteData.id 
                });
              } else {
                console.log("Skipping school addition:", { 
                  schoolID, 
                  reason: !schoolID ? "Missing schoolID" : 
                          typeof schoolID !== 'string' ? "Invalid schoolID type" : 
                          "Duplicate schoolID"
                });
              }
            }
          }
        }

        console.log("Schools loaded:", { schoolList, count: schoolList.length });
        setSchools([...schoolList]); // Ensure new array reference
      } catch (err: any) {
        console.error("Error fetching schools:", {
          message: err.message,
          code: err.code,
          details: err.details,
          stack: err.stack
        });
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load schools list. Check console for details.",
        });
      } finally {
        console.log("Setting loadingSchools to false");
        setLoadingSchools(false);
      }
    };

    if (isAuthenticated) {
      console.log("Calling fetchSchools");
      fetchSchools();
    } else {
      console.log("Skipping fetchSchools: not authenticated");
    }
  }, [email, isAuthenticated]);

  // Log schools state changes
  useEffect(() => {
    console.log("Schools state updated:", { schools, count: schools.length });
  }, [schools]);

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        if (!email || !isAuthenticated || !auth.currentUser || !hasValidClaims) {
          console.log("Skipping fetchStudents: missing prerequisites", {
            email,
            isAuthenticated,
            currentUser: !!auth.currentUser,
            hasValidClaims
          });
          setLoadingStudents(false);
          return;
        }
        setLoadingStudents(true);
        console.log("Fetching user claims for student data:", { uid: auth.currentUser.uid });
        
        const getGuardianStudents = httpsCallable(functions, 'getGuardianStudents');
        const result = await getGuardianStudents();
        const data = result.data as { students: StudentItem[] };
        
        console.log("Students loaded:", data.students);
        setStudents(data.students || []);
        
        // Check for persisted preference first
        if (auth.currentUser && !selectedStudent) {
          const userPrefsRef = doc(db, "users", auth.currentUser.uid, "preferences", "general");
          const prefsSnap = await getDoc(userPrefsRef);
          
          if (prefsSnap.exists() && prefsSnap.data().lastSelectedStudent) {
            const lastSelected = prefsSnap.data().lastSelectedStudent;
            const matchingStudent = data.students.find(s => s.studentUid === lastSelected.studentUid);
            
            if (matchingStudent) {
              console.log("Restoring persisted student selection:", matchingStudent.firstName);
              dispatch(setSelectedStudent({
                studentUid: matchingStudent.studentUid,
                firstName: matchingStudent.firstName,
                lastName: matchingStudent.lastName,
                registrationNumber: matchingStudent.registrationNumber,
                schoolID: (matchingStudent as any).schoolID
              }));
              return;
            }
          }
        }

        // We no longer auto-select the first student here. 
        // DataPrefetcher handles the initial selection to ensure consistency across all pages.
      } catch (err: any) {
        console.error("Error fetching students:", err);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load student data. Check console for details.",
        });
      } finally {
        console.log("Setting loadingStudents to false");
        setLoadingStudents(false);
      }
    };

    if (isAuthenticated && hasValidClaims) {
      fetchStudents();
    } else if (isAuthenticated && !hasValidClaims) {
      // If they are logged in but have no school/student claims, they won't have students
      setLoadingStudents(false);
    }
  }, [email, isAuthenticated, hasValidClaims]);

  const handleEnrollClick = async () => {
    if (!isAuthenticated || !auth.currentUser) {
      console.log("Authentication check failed:", { isAuthenticated, currentUser: !!auth.currentUser });
      toast({
        variant: "destructive",
        title: "Authentication Error",
        description: "User not authenticated. Please log in again.",
      });
      return;
    }
    if (!selectedSchool) {
      console.log("No school selected");
      toast({
        variant: "destructive",
        title: "Selection Error",
        description: "Please select a school.",
      });
      return;
    }

    setIsEnrolling(true);
    console.log("handleEnrollClick started:", { email, selectedSchool, uid: auth.currentUser.uid });
    try {
      // Find the selected school in our state to get the inviteId
      const selectedSchoolData = schools.find(s => s.id === selectedSchool);
      
      if (!selectedSchoolData) {
        console.log("No matching invitation found in state");
        toast({
          variant: "destructive",
          title: "Invitation Error",
          description: "No valid invitation found for this school.",
        });
        return;
      }

      const inviteId = selectedSchoolData.inviteId;
      console.log("Invitation data:", {
        inviteId,
        schoolID: selectedSchoolData.id,
        role: selectedSchoolData.role,
        schoolName: selectedSchoolData.schoolName
      });

      console.log("Calling acceptInvitation with:", { inviteId });
      const acceptInvitation = httpsCallable(functions, "acceptInvitation");
      const result = await acceptInvitation({ inviteId });
      const data = result.data as { schoolID: string; message: string };
      console.log("acceptInvitation response:", data);

      if (data.schoolID !== selectedSchool) {
        console.log("SchoolID mismatch in response:", { expected: selectedSchool, received: data.schoolID });
        throw new Error("Failed to set schoolID correctly.");
      }

      await auth.currentUser.getIdToken(true);
      console.log("Token refreshed after acceptInvitation");
      setHasValidClaims(true); // Trigger fetchStudents after new claims

      // Show modal popup
      setEnrolledSchoolName(selectedSchoolData.schoolName || "Unnamed School");
      setShowSuccessModal(true);

      toast({
        title: data.message,
        description: `Enrollment completed at ${new Date().toLocaleTimeString("en-US", { timeZone: "Africa/Nairobi" })} EAT, ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}.`,
        duration: 4000,
      });
    } catch (err: any) {
      console.error("Enrollment error:", {
        message: err.message,
        code: err.code,
        details: err.details,
        stack: err.stack
      });
      toast({
        variant: "destructive",
        title: "Enrollment Failed",
        description: "Failed to enroll. Check console for details.",
      });
    } finally {
      console.log("Enrollment process completed, resetting isEnrolling");
      setIsEnrolling(false);
    }
  };

  const handleStudentClick = (student: StudentItem) => {
    if (selectedStudent?.studentUid === student.studentUid) return;
    setPendingStudentSelection(student);
    setConfirmDialogOpen(true);
  };

  const confirmStudentSwitch = async () => {
    if (!pendingStudentSelection || !auth.currentUser) return;

    const student = pendingStudentSelection;
    const schoolID = (student as any).schoolID;

    try {
      const studentToSelect = {
        studentUid: student.studentUid,
        firstName: student.firstName,
        lastName: student.lastName,
        registrationNumber: student.registrationNumber,
        schoolID: schoolID
      };

      // 1. Update Redux State
      dispatch(setSelectedStudent(studentToSelect));

      // 2. Persist to Firebase (User Preferences)
      const userPrefsRef = doc(db, "users", auth.currentUser.uid, "preferences", "general");
      await setDoc(userPrefsRef, {
        lastSelectedStudent: studentToSelect,
        updatedAt: new Date()
      }, { merge: true });

      // 3. Persist to localStorage for instant restoration on refresh
      localStorage.setItem(`lastStudent_${auth.currentUser.uid}`, JSON.stringify(studentToSelect));

      toast({
        title: "Student Switched",
        description: `Active student is now ${student.firstName} ${student.lastName}`,
        duration: 3000,
      });
    } catch (error) {
      console.error("Error saving student preference:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save your selection. Please try again.",
      });
    } finally {
      setConfirmDialogOpen(false);
      setPendingStudentSelection(null);
    }
  };

  // Log dropdown rendering
  console.log("Rendering dropdown:", { 
    loadingSchools, 
    isAuthenticated, 
    schools, 
    selectedSchool,
    dropdownDisabled: loadingSchools || !isAuthenticated 
  });

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full">
        <div className="hidden lg:block w-64 h-full border-r border-gray-200 dark:border-gray-700">
          <Teachersidebar />
        </div>
        <div className="flex flex-col flex-1 h-full">
          <Teacherheader />
          <main className="flex-1 overflow-auto p-6 bg-gray-50 dark:bg-[#0f1117]">
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                ParentSettings
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Manage your account and enrollment settings
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="rounded-xl border border-gray-200 dark:border-[#2C2C3B] bg-white dark:bg-[#1E1E2D]">
                <CardHeader>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                      <School className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white">
                        Enrolled Schools
                      </CardTitle>
                      <CardDescription className="text-gray-600 dark:text-gray-400">
                        Schools you are currently associated with
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {enrolledSchools.length === 0 ? (
                    <p className="text-gray-600 dark:text-gray-400">You are not enrolled in any schools yet.</p>
                  ) : (
                    <ul className="space-y-2">
                      {enrolledSchools.map((school) => (
                        <li key={school.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          <span className="text-gray-900 dark:text-white font-medium">{school.schoolName}</span>
                          <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">Guardian</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>

              <Card className="rounded-xl border border-gray-200 dark:border-[#2C2C3B] bg-white dark:bg-[#1E1E2D]">
                <CardHeader>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                      <UserPlus className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white">
                        Enroll to New School
                      </CardTitle>
                      <CardDescription className="text-gray-600 dark:text-gray-400">
                        Accept pending invitations
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="dark:text-gray-300">
                      Email Address
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      readOnly
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="school">Select Invitation</Label>
                    <Select
                      value={selectedSchool}
                      onValueChange={setSelectedSchool}
                      disabled={loadingSchools || !isAuthenticated}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={loadingSchools ? "Loading…" : "Choose a school"}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {schools.length === 0 ? (
                          <div className="px-4 py-2 text-gray-500 text-sm">No pending invitations found</div>
                        ) : (
                          schools.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.schoolName}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={handleEnrollClick}
                    className="w-full bg-blue-600 text-white mt-4 hover:bg-blue-700"
                    disabled={!email || !selectedSchool || isEnrolling || !isAuthenticated}
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    {isEnrolling ? "Enrolling..." : "Accept Invitation"}
                  </Button>
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                    Your email must match an invitation sent by the school admin.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="rounded-xl border border-gray-200 dark:border-[#2C2C3B] bg-white dark:bg-[#1E1E2D]">
                <CardHeader>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gray-500 rounded-xl flex items-center justify-center">
                      <Settings className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white">
                        Linked Students
                      </CardTitle>
                      <CardDescription className="text-gray-600 dark:text-gray-400">
                        Students you are authorized to view
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingStudents ? (
                    <p className="text-gray-600 dark:text-gray-400">Loading students...</p>
                  ) : students.length === 0 ? (
                    <p className="text-gray-600 dark:text-gray-400">No linked students found.</p>
                  ) : (
                    <ul className="">
                      {students.map((student) => {
                        const isSelected = selectedStudent?.studentUid === student.studentUid;
                        return (
                          <li 
                            key={student.studentUid} 
                            className={`flex items-center justify-between p-4 rounded-xl cursor-pointer transition-all duration-200 m-2 border ${
                              isSelected 
                                ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 shadow-sm ring-1 ring-blue-200 dark:ring-blue-800" 
                                : "bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 hover:border-blue-200 dark:hover:border-blue-700 hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-700/50"
                            }`}
                            onClick={() => handleStudentClick(student)}
                          >
                            <div className="flex flex-col">
                              <span className="text-gray-900 dark:text-white font-medium">
                                {student.firstName} {student.lastName}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {student.registrationNumber}
                              </span>
                            </div>
                            {isSelected && <Check className="h-4 w-4 text-blue-600 dark:text-blue-400" />}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>
            
            <ConfirmDialog
              open={confirmDialogOpen}
              title="Switch Active Student?"
              description={`Are you sure you want to switch to ${pendingStudentSelection?.firstName} ${pendingStudentSelection?.lastName}? This will change the data shown in your dashboard and finance views.`}
              onConfirm={confirmStudentSwitch}
              onCancel={() => {
                setConfirmDialogOpen(false);
                setPendingStudentSelection(null);
              }}
              confirmText="Yes, Switch"
              cancelText="Cancel"
            />

            {/* Success Modal Popup */}
            {showSuccessModal && (
              <div 
                className="fixed inset-0 z-50 flex items-center justify-center"
                style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
              >
                <div className="bg-white dark:bg-[#23233a] rounded-xl shadow-xl p-8 w-full max-w-md mx-auto flex flex-col items-center">
                  <div className="flex items-center justify-center mb-4">
                    <UserPlus className="h-8 w-8 text-green-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2 text-center">Successfully Enrolled!</h2>
                  <p className="text-gray-700 dark:text-gray-300 text-center mb-4">
                    You have been successfully enrolled as a <span className="font-semibold text-blue-600">guardian</span> at <span className="font-semibold text-green-600">{enrolledSchoolName}</span>.
                  </p>
                  <button
                    onClick={() => {
                      setShowSuccessModal(false);
                      window.location.href = "/guardian-dashboard";
                    }}
                    className="mt-2 px-6 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 focus:outline-none"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default ParentSettings;